import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRightIcon, MailIcon, PackageSearchIcon, TruckIcon, ClockIcon } from 'lucide-react';
import { PageHeader } from '../components/PageHeader';
import { SiteFooter } from '../components/SiteFooter';
import { UspsBadge } from '../components/UspsBadge';
import { isLikelyTrackingNumber, parseTrackingNumbers, uspsTrackingUrl } from '../utils/usps';
import { trackCustom } from '../utils/tracking';

/** /track · enter a USPS tracking number here, see the full transit history on USPS. */
export function TrackOrder() {
  const [value, setValue] = useState('');
  const [error, setError] = useState('');
  const [opened, setOpened] = useState<string | null>(null);
  useEffect(() => {
    window.scrollTo(0, 0);
    trackCustom('track_order_page_visited');
  }, []);
  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    const numbers = parseTrackingNumbers(value);
    if (numbers.length === 0) {
      setError('Enter your tracking number.');
      return;
    }
    if (!numbers.every(isLikelyTrackingNumber)) {
      setError('That doesn’t look like a USPS tracking number. Check your shipping email and try again.');
      return;
    }
    setError('');
    const url = uspsTrackingUrl(numbers);
    setOpened(url);
    trackCustom('track_order_submitted', {
      count: numbers.length
    });
    const win = window.open(url, '_blank', 'noopener,noreferrer');
    // If the browser blocked the new tab, go there in this one.
    if (!win) window.location.href = url;
  };
  return <div className="min-h-full w-full bg-ink-950 font-sans text-offwhite" data-id="element-1842" data-mp="6kru9iv1pxe5">
      <PageHeader data-id="element-1843" data-mp="kjzsa8fh2zgr" />
      <main className="mx-auto max-w-[1400px] px-4 py-14 sm:px-6 lg:px-8 lg:py-20" data-id="element-1844" data-mp="kgbzxclrkj3m">
        <div className="grid gap-10 lg:grid-cols-[1fr_420px] lg:items-start" data-id="element-1845" data-mp="gfjoccpf6246">
          <div data-id="element-1846" data-mp="1vwae1uvstvbp">
            <p className="flex items-center gap-2 font-display text-xl font-extrabold uppercase tracking-wide text-grit-500" data-id="element-1847" data-mp="1vw0ou9ikvs72">
              <PackageSearchIcon className="h-6 w-6" aria-hidden="true" data-id="element-1848" data-mp="1ryclci2tkr3k" />
              Track my order
            </p>
            <h1 className="grit-headline mt-3 text-[clamp(2.25rem,7vw,4.25rem)] font-extrabold uppercase text-offwhite" data-id="element-1849" data-mp="tjxdnfglohik">
              Where&apos;s my X1?
            </h1>
            <p className="mt-3 max-w-xl text-lg text-steel-300" data-id="element-1850" data-mp="yh60htcp19q5">
              Paste your USPS tracking number. We&apos;ll open USPS with your package&apos;s full transit history.
            </p>

            <form onSubmit={submit} noValidate className="mt-8 max-w-xl border border-white/12 bg-ink-900 p-5 sm:p-7" data-id="element-1851" data-mp="5si8zg1cpo45">
              <label htmlFor="tracking-number" className="text-[11px] font-bold uppercase tracking-[0.2em] text-steel-400" data-id="element-1852" data-mp="evavz73i3do6">
                USPS tracking number
              </label>
              <input id="tracking-number" type="text" inputMode="text" autoComplete="off" autoCapitalize="characters" spellCheck={false} value={value} onChange={event => {
              setValue(event.target.value);
              if (error) setError('');
            }} placeholder="e.g. 9400 1000 0000 0000 0000 00" aria-invalid={error ? true : undefined} aria-describedby={error ? 'tracking-error' : 'tracking-help'} className="mt-2 w-full border-2 border-white/20 bg-ink-950 px-4 py-3.5 font-mono text-[16px] tracking-wide text-offwhite placeholder:text-steel-500 focus:border-grit-500 focus:outline-none" data-id="element-1853" data-mp="jnud2e1ecqxq" />
              {error ? <p id="tracking-error" role="alert" className="mt-2 text-[13px] font-semibold text-[#ff8a7a]" data-id="element-1854" data-mp="2n3xuuwqhg4r0">
                  {error}
                </p> : <p id="tracking-help" className="mt-2 text-[13px] text-steel-400" data-id="element-1855" data-mp="2e2rd6kc3knw6">
                  Spaces are fine. More than one package? Separate numbers with commas.
                </p>}
              <button type="submit" className="mt-5 inline-flex w-full items-center justify-center gap-2 bg-grit-500 px-6 py-4 font-display text-xl font-extrabold uppercase tracking-wide text-ink-950 transition-[background-color,transform] duration-150 ease-grit hover:bg-grit-400 active:translate-y-[2px] focus:outline-none focus-visible:ring-2 focus-visible:ring-offwhite sm:w-auto" data-id="element-1856" data-mp="czqkvebl6wat">
                Track on USPS
                <ArrowUpRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-1857" data-mp="3qh9a325o49cs" />
              </button>
              {opened && <p className="mt-4 text-[13px] text-steel-300" data-id="element-1858" data-mp="3gh8o18gezf4w">
                  USPS opened in a new tab.{' '}
                  <a href={opened} target="_blank" rel="noopener noreferrer" className="font-semibold text-grit-500 underline underline-offset-2" data-id="element-1859" data-mp="3lgv37nfsz5gc">
                    Didn&apos;t open? Tap here.
                  </a>
                </p>}
            </form>
          </div>

          <aside className="grid gap-px bg-white/10 border border-white/12" data-id="element-1860" data-mp="345y90c58wnys">
            <div className="bg-ink-900 p-5" data-id="element-1861" data-mp="2z7engcukde6y">
              <p className="font-display text-lg font-bold uppercase text-offwhite" data-id="element-1862" data-mp="2z8pnc1kw21ss">Where do I find it?</p>
              <p className="mt-1.5 text-[14px] leading-relaxed text-steel-300" data-id="element-1863" data-mp="3dxu73n7c0e30">
                In the shipping confirmation email we send when your order leaves the warehouse. Check spam or
                promotions if you don&apos;t see it.
              </p>
            </div>
            <div className="flex items-start gap-3 bg-ink-900 p-5" data-id="element-1864" data-mp="3hwun6xyfd2mb">
              <ClockIcon className="mt-0.5 h-5 w-5 shrink-0 text-grit-500" aria-hidden="true" data-id="element-1865" data-mp="phdsdm50bi7" />
              <p className="text-[14px] leading-relaxed text-steel-300" data-id="element-1866" data-mp="3rw28q8fbc3ki">
                Orders placed before 2pm CT ship the same business day and typically arrive in 3 to 6 business days.
                Tracking can take up to 24 hours to show the first scan.
              </p>
            </div>
            <div className="flex items-start gap-3 bg-ink-900 p-5" data-id="element-1867" data-mp="2n66jy5j4u4qt">
              <TruckIcon className="mt-0.5 h-5 w-5 shrink-0 text-grit-500" aria-hidden="true" data-id="element-1868" data-mp="2n7zonfa6qew6" />
              <div data-id="element-1869" data-mp="2r81rvsvkjgdk">
                <p className="text-[14px] leading-relaxed text-steel-300" data-id="element-1870" data-mp="2r9l85jh85rfc">Shipped USPS from the U.S., insured and tracked.</p>
                <div className="mt-3" data-id="element-1871" data-mp="316h9n58phdae">
                  <UspsBadge data-id="element-1872" data-mp="317iw05v69m7f" />
                </div>
              </div>
            </div>
            <div className="bg-ink-900 p-5" data-id="element-1873" data-mp="3649baqz5kdrs">
              <p className="font-display text-lg font-bold uppercase text-offwhite" data-id="element-1874" data-mp="3a6mhfghtadp3">No tracking number yet?</p>
              <a href="mailto:support@gritvuegear.com?subject=Where%20is%20my%20order%3F" className="mt-2 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-offwhite transition-colors duration-150 ease-grit hover:text-grit-500" data-id="element-1875" data-mp="3exmeclqxkvrl">
                <MailIcon className="h-4 w-4 text-grit-500" aria-hidden="true" data-id="element-1876" data-mp="3tuddqv0lu7h7" />
                support@gritvuegear.com
              </a>
              <p className="mt-2 text-[13px] text-steel-400" data-id="element-1877" data-mp="1i81gk180zu1y">
                Include your order number. <Link to="/legal/shipping" className="underline underline-offset-2 hover:text-offwhite" data-id="element-1878" data-mp="1d75cnnnoc7wj">Shipping policy</Link>
              </p>
            </div>
          </aside>
        </div>
      </main>
      <SiteFooter data-id="element-1879" data-mp="13barolvza0ql" />
    </div>;
}